import React, { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import SettingsIcon from '@mui/icons-material/Settings';
import './Layout.scss';
import { useTranslation } from 'react-i18next';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    ListItemText,
    Collapse,
    Popper,
    Grow,
    Paper,
    ClickAwayListener,
    MenuList,
    MenuItem,
    Dialog,
    DialogContent,
    Stack,
} from '@mui/material';
import { AvatarIcon } from 'assets/icons/avatar-icon';
import { LeftArrowIcon } from 'assets/icons/left-arrow-icon';
import { RightArrowIcon } from 'assets/icons/right-arrow-icon';
import { useDisclosure } from 'hook/useDisclosure';
import { ROUTES_PRIVATE, ROUTES_SUPER_ADMIN } from 'utils/routes';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { ALERT_TYPE, TKeys, USER_ROLE } from 'utils/enums';
import { AppDialogTitle, AppRadioGroup, DialogButtons } from 'components/common';
import { getVersion, logout } from 'utils/helpers';
import { updateUserFetcher } from 'components/pages/user/user.fetcher';
import { showAlert } from 'reducer/rootReducer';
import { apiService } from 'libs/axios/services';
import { BASE_URL } from 'libs/axios/axiosInstance';
import { getThreshold } from 'components/pages/threshold/threshold.services';
import { setThresholds } from 'reducer/userReducer';
import { THRESHOLD_DEFAULT } from 'utils/constants';

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

const DrawerFooter = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

const StyledLink = styled(Link)(() => ({
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
        textDecoration: 'none',
    },
}));

const BaseLayout: React.FC = () => {
    const { open, handleClose, handleOpen } = useDisclosure(true);
    const user = useSelector((state: RootState) => state.user.user);
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const drawerWidth = open ? 300 : 0;

    const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

    const [openSetting, setOpenSetting] = React.useState(false);
    const [openLogoutDialog, setOpenLogoutDialog] = React.useState(false);
    const [openLanguageDialog, setOpenLanguageDialog] = React.useState(false);
    const [languageSelected, setLanguageSelected] = React.useState<string>('');
    const [languages, setLanguages] = React.useState<ILanguage[]>([]);
    const [languageOptions, setLanguageOptions] = React.useState<IOption[]>([]);
    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const handleToggleSetting = () => {
        setOpenSetting((prevOpen) => !prevOpen);
    };

    const handleCloseSetting = (event: Event | React.SyntheticEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpenSetting(false);
    };

    const handleListKeyDownSetting = (event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpenSetting(false);
        } else if (event.key === 'Escape') {
            setOpenSetting(false);
        }
    };

    const prevOpen = React.useRef(openSetting);
    useEffect(() => {
        if (prevOpen.current === true && openSetting === false) {
            anchorRef.current!.focus();
        }

        prevOpen.current = openSetting;
    }, [openSetting]);

    const authorities = user?.authorities ?? [];
    const isUserRole99 = `${user?.role}` === `${USER_ROLE.MASTER_ADMIN}`;
    const isUserRoleSYSAdmin = `${user?.role}` === `${USER_ROLE.SYS_ADMIN}`;
    const isUserRoleAdmin = `${user?.role}` === `${USER_ROLE.ADMIN}`;

    const handleClick = (item: IMenuItem) => () => {
        const hasChildren = item?.CHILDREN && Object.keys(item?.CHILDREN).length > 0 ? true : false;

        if (hasChildren) {
            handleToggle(item.PATH);
        } else if (location.pathname !== item.PATH) {
            navigate(item.PATH);
        }
    };

    const handleToggle = (path: string) => {
        setOpenMenus((prev) => ({
            ...prev,
            [path]: !prev[path],
        }));
    };

    const handleOnClickLogout = (data: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        handleCloseSetting(data);

        setOpenLogoutDialog(true);
    };

    const handlePrintSetting = (data: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        handleCloseSetting(data);

        navigate('/print-setting');
    };

    const handleThresholdSetting = (data: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        handleCloseSetting(data);

        navigate('/threshold-setting');
    };

    const handleExternalApiKeySetting = (data: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        handleCloseSetting(data);

        navigate(ROUTES_PRIVATE.EXTERNAL_API_KEY.PATH);
    };

    const handleExternalIpWhitelistSetting = (
        data: React.MouseEvent<HTMLLIElement, MouseEvent>,
    ) => {
        handleCloseSetting(data);

        navigate(ROUTES_PRIVATE.EXTERNAL_IP_WHITELIST.PATH);
    };

    const handleOnClickSettingLanguage = (data: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        handleCloseSetting(data);

        setLanguageSelected(user?.language ?? '');

        setOpenLanguageDialog(true);
    };

    const handleChangeSettingLanguage = async () => {
        setOpenLanguageDialog(false);

        if (!user) return;

        const dataReq: ICreateUserReq = {
            user_id: user.user_id,
            language_id: languages.find((e) => e.code === languageSelected)?.id ?? 1,
        };

        const res = await updateUserFetcher(user.user_id, dataReq);

        if (!res.success) {
            handleShowAlert(res.error, ALERT_TYPE.ERROR);
            return;
        }

        handleShowAlert(res.message, ALERT_TYPE.SUCCESS);

        handleLogout();
    };

    const handleLogout = async () => {
        await dispatch(logout());
        navigate('/');
    };

    const handleShowAlert = useCallback(
        (message: string, type: ALERT_TYPE) => {
            dispatch(
                showAlert({
                    status: true,
                    message,
                    type,
                }),
            );
        },
        [dispatch],
    );

    const renderDialogConfirmLogout = () => {
        return (
            <Dialog
                open={openLogoutDialog}
                onClose={(_, reason) => {
                    if (reason === 'backdropClick') return;
                }}
                disableEscapeKeyDown
                autoFocus
                maxWidth="sm"
                fullWidth>
                <AppDialogTitle title={t(TKeys.LBL050071)} />

                <DialogContent>
                    <Stack direction="column" padding={1} spacing={2}>
                        <Typography
                            sx={{
                                fontFamily: 'Meiryo',
                                fontWeight: '400',
                                fontSize: '20px',
                                lineHeight: '30px',
                                textAlign: 'center',
                            }}>
                            {t(TKeys.LBL000006)}
                        </Typography>
                    </Stack>
                </DialogContent>

                <DialogButtons
                    aria-label="Logout"
                    onClose={() => setOpenLogoutDialog(false)}
                    cancelLabel={t(TKeys.BTN000016)}
                    submitLabel={t(TKeys.BTN000017)}
                    onSubmit={handleLogout}
                />
            </Dialog>
        );
    };

    const renderDialogSettingLanguage = () => {
        return (
            <Dialog
                open={openLanguageDialog}
                onClose={(_, reason) => {
                    if (reason === 'backdropClick') return;
                }}
                disableEscapeKeyDown
                autoFocus
                maxWidth="sm"
                fullWidth>
                <AppDialogTitle title={t(TKeys.LBL000005)} />

                <DialogContent>
                    <Stack direction="column" padding={1} spacing={2}>
                        <Stack direction="row">
                            <AppRadioGroup
                                dataRadio={languageOptions}
                                row
                                sxRadioGroup={{
                                    justifyContent: 'space-between',
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${languageOptions.length === 1 ? '1' : languageOptions.length === 2 ? '2' : '3'}, 1fr)`,
                                }}
                                onChangeCallback={(_, value) => {
                                    setLanguageSelected(value);
                                }}
                                value={languageSelected}
                            />
                        </Stack>
                    </Stack>
                </DialogContent>

                <DialogButtons
                    onClose={() => setOpenLanguageDialog(false)}
                    cancelLabel={t(TKeys.BTN000016)}
                    submitLabel={t(TKeys.BTN000017)}
                    onSubmit={handleChangeSettingLanguage}
                />
            </Dialog>
        );
    };

    const fetchLanguageOptions = async () => {
        const res = await apiService.get<URLSearchParams, IGetLanguagesRes>(
            `${BASE_URL}/language/list`,
        );

        if (!res.success) return;

        const { data } = res.data;
        setLanguages(data);
        const tempOptions: IOption[] = [];
        data.map((e) =>
            tempOptions.push({
                value: e.code,
                label: e.name,
            }),
        );

        setLanguageOptions(tempOptions);
    };

    const fetchThreshold = async () => {
        const res = await getThreshold();
        const hasData = res?.success && res?.data && res?.data?.data;
        dispatch(setThresholds(hasData ? res.data.data : THRESHOLD_DEFAULT));
    };

    useEffect(() => {
        fetchLanguageOptions();
        fetchThreshold();
        // eslint-disable-next-line
    }, []);

    const renderDrawer = () => {
        const isShowPrintSetting = isUserRoleSYSAdmin || isUserRoleAdmin;
        // CR #28: external integration (API key / IP whitelist) is a global, cross-contract
        // resource -> only MASTER_ADMIN may manage it (ADMIN/SYS_ADMIN are contract-scoped).
        const isShowExternalSetting = isUserRole99;
        return (
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    height: '100vh',
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        height: '100vh',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                }}
                variant="persistent"
                anchor="left"
                open={open}>
                <DrawerHeader
                    sx={{
                        backgroundColor: 'var(--color-menu)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'center',
                        }}>
                        <IconButton sx={{ height: 50, width: 50 }} aria-label="Avatar">
                            <AvatarIcon />
                        </IconButton>
                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{
                                flexGrow: 1,
                                color: 'var(--color-white)',
                                fontSize: 25,
                                maxWidth: '180px',
                                width: '100%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            {user?.user_name ?? ''}
                        </Typography>
                    </Box>
                    <>
                        <IconButton
                            ref={anchorRef}
                            id="composition-button"
                            aria-label="Toggle Drawer"
                            aria-controls={open ? 'composition-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-haspopup="true"
                            onClick={handleToggleSetting}>
                            <SettingsIcon
                                fontSize="large"
                                sx={{ cursor: 'pointer', color: 'var(--color-white)' }}
                            />
                        </IconButton>

                        {/* Setting */}
                        <Popper
                            open={openSetting}
                            anchorEl={anchorRef.current}
                            role={undefined}
                            placement="bottom-start"
                            transition
                            sx={{
                                zIndex: 2,
                            }}
                            disablePortal>
                            {({ TransitionProps, placement }) => (
                                <Grow
                                    {...TransitionProps}
                                    style={{
                                        transformOrigin:
                                            placement === 'bottom-start'
                                                ? 'left top'
                                                : 'left bottom',
                                    }}>
                                    <Paper>
                                        <ClickAwayListener onClickAway={handleCloseSetting}>
                                            <MenuList
                                                autoFocusItem={open}
                                                id="composition-menu"
                                                aria-labelledby="composition-button"
                                                onKeyDown={handleListKeyDownSetting}>
                                                <MenuItem onClick={handleOnClickSettingLanguage}>
                                                    {t(TKeys.LBL000005)}
                                                </MenuItem>
                                                {isShowPrintSetting && [
                                                    <Divider key="divider-print" component="li" />,
                                                    <MenuItem
                                                        key="print-setting"
                                                        onClick={handlePrintSetting}>
                                                        {t(TKeys.LBL000009)}
                                                    </MenuItem>,
                                                ]}
                                                <Divider component="li" />
                                                <MenuItem
                                                    key="threshold-setting"
                                                    onClick={handleThresholdSetting}>
                                                    {t(TKeys.LBL270015)}
                                                </MenuItem>
                                                {isShowExternalSetting && [
                                                    <Divider
                                                        key="divider-external-api"
                                                        component="li"
                                                    />,
                                                    <MenuItem
                                                        key="external-api-key"
                                                        onClick={handleExternalApiKeySetting}>
                                                        {t(TKeys.LBL290001)}
                                                    </MenuItem>,
                                                    <MenuItem
                                                        key="external-ip-whitelist"
                                                        onClick={handleExternalIpWhitelistSetting}>
                                                        {t(TKeys.LBL290002)}
                                                    </MenuItem>,
                                                ]}
                                                <Divider component="li" />
                                                <MenuItem onClick={handleOnClickLogout}>
                                                    {t(TKeys.LBL050071)}
                                                </MenuItem>
                                            </MenuList>
                                        </ClickAwayListener>
                                    </Paper>
                                </Grow>
                            )}
                        </Popper>
                    </>
                </DrawerHeader>

                <Divider />

                <Box sx={{ flexGrow: 1, overflowY: 'auto', backgroundColor: 'var(--color-menu)' }}>
                    <List sx={{ backgroundColor: 'var(--color-menu)', padding: [0, '10px'] }}>
                        {isUserRole99
                            ? Object.values(ROUTES_SUPER_ADMIN).map((item: IMenuItem) => {
                                  const isActive = location.pathname === item?.PATH;
                                  const hasChildren =
                                      item?.CHILDREN && Object.keys(item?.CHILDREN).length > 0
                                          ? true
                                          : false;

                                  return (
                                      <React.Fragment key={item.PATH}>
                                          <ListItem
                                              sx={{
                                                  bgcolor:
                                                      isActive && !hasChildren
                                                          ? 'var(--color-header-table)'
                                                          : 'transparent',
                                                  borderRadius: '4px',
                                                  cursor: 'pointer',
                                                  '&:hover': {
                                                      backgroundColor: 'var(--color-header-table)',
                                                  },
                                              }}
                                              onClick={handleClick(item)}>
                                              <ListItemText>
                                                  {hasChildren ? (
                                                      <ListItemText
                                                          sx={{
                                                              color: 'var(--color-white)',
                                                              fontSize: 18,
                                                          }}>
                                                          {t(item.NAME)}
                                                      </ListItemText>
                                                  ) : (
                                                      <StyledLink
                                                          to={item.PATH}
                                                          sx={{
                                                              color: 'var(--color-white)',
                                                              fontSize: 18,
                                                          }}>
                                                          {t(item.NAME)}
                                                      </StyledLink>
                                                  )}
                                              </ListItemText>

                                              {hasChildren ? (
                                                  openMenus[item.PATH] ? (
                                                      <ExpandLess
                                                          sx={{ color: 'var(--color-white)' }}
                                                      />
                                                  ) : (
                                                      <ExpandMore
                                                          sx={{ color: 'var(--color-white)' }}
                                                      />
                                                  )
                                              ) : null}
                                          </ListItem>

                                          {hasChildren && (
                                              <Collapse
                                                  in={openMenus[item.PATH]}
                                                  timeout="auto"
                                                  unmountOnExit>
                                                  <List sx={{ paddingLeft: 4 }}>
                                                      {item?.CHILDREN &&
                                                          Object.values(item?.CHILDREN)?.map(
                                                              (child: IMenuItem) => {
                                                                  const isChildActive =
                                                                      location.pathname ===
                                                                      child.PATH;
                                                                  return (
                                                                      <ListItem
                                                                          key={child.PATH}
                                                                          sx={{
                                                                              bgcolor: isChildActive
                                                                                  ? 'var(--color-header-table)'
                                                                                  : 'transparent',
                                                                              borderRadius: '4px',
                                                                              cursor: 'pointer',
                                                                              '&:hover': {
                                                                                  backgroundColor:
                                                                                      'var(--color-header-table)',
                                                                              },
                                                                          }}
                                                                          onClick={handleClick(
                                                                              child,
                                                                          )}>
                                                                          <ListItemText>
                                                                              <StyledLink
                                                                                  to={child.PATH}
                                                                                  sx={{
                                                                                      color: 'var(--color-white)',
                                                                                      fontSize: 16,
                                                                                  }}>
                                                                                  {t(child.NAME)}
                                                                              </StyledLink>
                                                                          </ListItemText>
                                                                      </ListItem>
                                                                  );
                                                              },
                                                          )}
                                                  </List>
                                              </Collapse>
                                          )}
                                      </React.Fragment>
                                  );
                              })
                            : Object.values(ROUTES_PRIVATE)
                                  .filter(({ ID }: IMenuItem) => authorities.includes(ID))
                                  .map((item: IMenuItem) => {
                                      const isActive = location.pathname === item?.PATH;
                                      const hasChildren =
                                          item?.CHILDREN && Object.keys(item?.CHILDREN).length > 0
                                              ? true
                                              : false;

                                      return (
                                          <React.Fragment key={item.PATH}>
                                              <ListItem
                                                  sx={{
                                                      bgcolor:
                                                          isActive && !hasChildren
                                                              ? 'var(--color-header-table)'
                                                              : 'transparent',
                                                      borderRadius: '4px',
                                                      cursor: 'pointer',
                                                      '&:hover': {
                                                          backgroundColor:
                                                              'var(--color-header-table)',
                                                      },
                                                  }}
                                                  onClick={handleClick(item)}>
                                                  <ListItemText>
                                                      {hasChildren ? (
                                                          <ListItemText
                                                              sx={{
                                                                  color: 'var(--color-white)',
                                                                  fontSize: 18,
                                                              }}>
                                                              {t(item.NAME)}
                                                          </ListItemText>
                                                      ) : (
                                                          <StyledLink
                                                              to={item.PATH}
                                                              sx={{
                                                                  color: 'var(--color-white)',
                                                                  fontSize: 18,
                                                              }}>
                                                              {t(item.NAME)}
                                                          </StyledLink>
                                                      )}
                                                  </ListItemText>

                                                  {hasChildren ? (
                                                      openMenus[item.PATH] ? (
                                                          <ExpandLess
                                                              sx={{ color: 'var(--color-white)' }}
                                                          />
                                                      ) : (
                                                          <ExpandMore
                                                              sx={{ color: 'var(--color-white)' }}
                                                          />
                                                      )
                                                  ) : null}
                                              </ListItem>

                                              {hasChildren && (
                                                  <Collapse
                                                      in={openMenus[item.PATH]}
                                                      timeout="auto"
                                                      unmountOnExit>
                                                      <List sx={{ paddingLeft: 4 }}>
                                                          {item?.CHILDREN &&
                                                              Object.values(item?.CHILDREN)?.map(
                                                                  (child: IMenuItem) => {
                                                                      const isChildActive =
                                                                          location.pathname ===
                                                                          child.PATH;
                                                                      return (
                                                                          <ListItem
                                                                              key={child.PATH}
                                                                              sx={{
                                                                                  bgcolor:
                                                                                      isChildActive
                                                                                          ? 'var(--color-header-table)'
                                                                                          : 'transparent',
                                                                                  borderRadius:
                                                                                      '4px',
                                                                                  cursor: 'pointer',
                                                                                  '&:hover': {
                                                                                      backgroundColor:
                                                                                          'var(--color-header-table)',
                                                                                  },
                                                                              }}
                                                                              onClick={handleClick(
                                                                                  child,
                                                                              )}>
                                                                              <ListItemText>
                                                                                  <StyledLink
                                                                                      to={
                                                                                          child.PATH
                                                                                      }
                                                                                      sx={{
                                                                                          color: 'var(--color-white)',
                                                                                          fontSize: 16,
                                                                                      }}>
                                                                                      {t(
                                                                                          child.NAME,
                                                                                      )}
                                                                                  </StyledLink>
                                                                              </ListItemText>
                                                                          </ListItem>
                                                                      );
                                                                  },
                                                              )}
                                                      </List>
                                                  </Collapse>
                                              )}
                                          </React.Fragment>
                                      );
                                  })}
                    </List>
                </Box>

                <DrawerFooter
                    sx={{
                        backgroundColor: 'var(--color-menu)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingBottom: '20px',
                        minHeight: '80px',
                        position: 'relative',
                        zIndex: 1,
                    }}>
                    <Box
                        sx={{
                            backgroundColor: 'var(--color-box-version)',
                            paddingX: '36px',
                            paddingY: '12px',
                            borderRadius: '40px',
                            cursor: 'pointer',
                            display: 'flex',
                            gap: '5px',
                            minWidth: '140px',
                            minHeight: '40px',
                            overflow: 'hidden',
                        }}>
                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{ color: 'var(--color-white)', fontSize: 18 }}>
                            {`ver${getVersion()}`}
                        </Typography>
                    </Box>
                </DrawerFooter>
            </Drawer>
        );
    };

    return (
        <Box sx={{ flex: 1, display: 'flex' }}>
            <CssBaseline />
            {renderDrawer()}
            <IconButton
                sx={{
                    position: 'fixed',
                    left: open ? 280 : 0,
                    bottom: 12,
                    cursor: 'pointer',
                    backgroundColor: 'var(--color-menu)',
                    paddingX: '25px',
                    paddingY: '25px',
                    borderRadius: '20px',
                    border: '2px solid var(--color-box-version)',
                    zIndex: 9999,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        opacity: 1,
                        backgroundColor: 'var(--color-menu)',
                        left: open ? 264 : 10,
                    },
                }}
                aria-label={open ? 'Close' : 'Open'}
                onClick={open ? handleClose : handleOpen}>
                {open ? <LeftArrowIcon /> : <RightArrowIcon />}
            </IconButton>
            <Box className="HomeLayout">
                {renderDialogConfirmLogout()}

                {renderDialogSettingLanguage()}

                <Outlet />
            </Box>
        </Box>
    );
};

export default BaseLayout;
